/* eslint-disable */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

const Card = ({ src, title, id, index, caption, moveImage, deleteImage, updateCaption, isNew }) => {
    const ref = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    const [, drop] = useDrop({
        accept: "image",
        hover: (item, monitor) => {
            if (!ref.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            moveImage(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: "image",
        item: { id, index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.3 : 1;
    drag(drop(ref));

    return (
        <div
            ref={ref}
            style={{
                opacity,
                cursor: "move",
                aspectRatio: "16/9",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#f9f9f9",
                marginBottom: "10px",
                position: "relative",
                transition: "all 0.2s ease-in-out",
                transform: isDragging ? "scale(1.05)" : "scale(1)",
                boxShadow: isHovered
                    ? "0 0 10px 2px rgba(0, 123, 255, 0.7)"
                    : "0 2px 4px rgba(0, 0, 0, 0.1)",
                border: isHovered ? "2px solid #007bff" : "2px solid transparent",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* "New" Tag */}
            {isNew && (
                <div
                    style={{
                        position: "absolute",
                        top: "8px",
                        left: "8px",
                        backgroundColor: "#28a745",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        zIndex: 10,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                >
                    NEW
                </div>
            )}

            <img
                src={src}
                alt={title}
                style={{
                    height: "80%",
                    width: "100%",
                    objectFit: "cover",
                }}
            />

            {/* Caption Input */}
            <input
                type="text"
                placeholder="Enter caption..."
                value={caption || ""}
                onChange={(e) => updateCaption(e.target.value)}
                style={{
                    width: "90%",
                    margin: "5px 0",
                    padding: "5px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "14px",
                }}
            />

            {/* Delete Button */}
            <button
                onClick={() => deleteImage(id)}
                style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    backgroundColor: "rgba(255, 0, 0, 0.7)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    fontSize: "18px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    zIndex: 10,
                }}
            >
                ×
            </button>
        </div>
    );
};


const ImageDragList = ({ data, setValue }) => {
    const [images, setImages] = useState(data || []);
    const [totalSizeMB, setTotalSizeMB] = useState(0);

    useEffect(() => {
        if (data) setImages(data);
    }, [data]);


    // ---- FILE SIZE CALCULATOR ----
    async function getFileSize(url) {
        try {
            const response = await fetch(url, { method: "HEAD" });
            const size = response.headers.get("content-length");
            return size ? parseInt(size, 10) : 0;
        } catch (error) {
            console.error("Error fetching file size:", error);
            return 0;
        }
    }

    async function calculateTotalSize(images) {
        const sizes = await Promise.all(
            images.map((img) =>
                img.size
                    ? Promise.resolve(img.size)
                    : img.link
                        ? getFileSize(img.link)
                        : Promise.resolve(0)
            )
        );
        const totalBytes = sizes.reduce((acc, size) => acc + size, 0);
        setTotalSizeMB(totalBytes / (1024 * 1024));
    }

    useEffect(() => {
        if (images.length > 0) calculateTotalSize(images);
    }, [images]);

    // ---- DRAG HANDLER ----
    const moveImage = useCallback((dragIndex, hoverIndex) => {
        setImages((prevImages) => {
            const updated = [...prevImages];
            const [draggedItem] = updated.splice(dragIndex, 1);
            updated.splice(hoverIndex, 0, draggedItem);

            // update positions (1-based index)
            const reIndexed = updated.map((img, i) => ({
                ...img,
                position: i,
            }));

            // sync external state
            setValue(reIndexed);
            return reIndexed;
        });
    }, [setValue]);

    const deleteImage = useCallback(
        (name) => {
            setImages((prev) => {
                // 1️⃣ Remove the image
                const filtered = prev.filter((img) => img.name !== name);

                // 2️⃣ Reindex positions
                const reIndexed = filtered.map((img, i) => ({
                    ...img,
                    position: i,
                }));

                // 3️⃣ Sync external state
                setValue(reIndexed);

                return reIndexed;
            });

        
        },
        [setValue]
    );

    return (
        <div
            style={{
                backgroundColor: "white",
                borderRadius: "4px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                width: "100%",
                display: "grid",
                gap: "10px",
                alignItems: "center",
                justifyContent: "center",
                gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
                padding: "20px",
            }}
        >
            <div style={{ gridColumn: "1 / -1", marginBottom: "10px", fontWeight: "bold" }}>
                {totalSizeMB >= 60 ? (
                    <span style={{ color: "red" }}>
                        Size Limit Exceeded: {totalSizeMB.toFixed(2)} MB / 60MB
                    </span>
                ) : (
                    <span>Size Limit: {totalSizeMB.toFixed(2)} MB / 60MB</span>
                )}
            </div>

            {images.map((image, index) => {
                const caption = image.caption;
                const src = image.data instanceof File ? URL.createObjectURL(image.data) : image.link;

                return (
                    <Card
                        key={image.name}
                        id={image.name}
                        index={index}
                        src={src}
                        title={image.name}
                        caption={caption}
                        isNew={image.new === true}
                        moveImage={moveImage}
                        deleteImage={deleteImage}
                        updateCaption={(captionData) => {
                            setImages((prevImages) => {
                                const newImages = prevImages.map((img, i) =>
                                    i === index ? { ...img, caption: captionData } : img
                                );

                                // sync external state
                                setValue(newImages);
                                return newImages;
                            });
                        }}
                    />
                );
            })}
        </div>
    );
};

export default ImageDragList;
